import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Download,
  FileText,
  Hammer,
  Upload,
} from "lucide-react";
import { Button, Card, Field, PageHeader, cx } from "../ui";

const STORAGE_KEY = "sudler_contractor_session";

/**
 * Reference templates for the documents this phase asks contractors
 * to fill out, sign, and upload as scanned PDFs. Keyed by document
 * type code so the contractor portal can surface a per-row "Download
 * template" link inline next to the upload control. Files live in
 * web/public/templates/ and Vite serves them at /templates/*.
 *
 * Phase 4 will replace the fill-sign-scan-upload loop with DocuSign
 * envelopes — these links go away then.
 */
const REQUIREMENT_TEMPLATES: Record<
  string,
  { label: string; url: string; format: string }
> = {
  rules_acknowledgement: {
    label: "Construction Rules & Regulations",
    url: "/templates/construction-rules-and-regulations.pdf",
    format: "PDF",
  },
  procedures_acknowledgement: {
    label: "Construction Packet (Procedures + Acknowledgement forms)",
    url: "/templates/construction-packet.pdf",
    format: "PDF",
  },
  permission_to_enter: {
    label: "Construction Packet (Permission to Enter form)",
    url: "/templates/construction-packet.pdf",
    format: "PDF",
  },
  contractor_coi: {
    label: "Insurance Requirements (give to your carrier)",
    url: "/templates/insurance-requirements.pdf",
    format: "PDF",
  },
  riser_shutdown_request: {
    label: "Riser Shutdown Request",
    url: "/templates/riser-shutdown-request.pdf",
    format: "PDF",
  },
  indemnity_agreement: {
    label: "Indemnity Agreement",
    url: "/templates/indemnity-agreement.pdf",
    format: "PDF",
  },
};

type RoleReq = {
  code: string;
  name: string;
  category: string;
  fulfilled: boolean;
  formatHint: string;
};

type PortalState = {
  project: {
    id: string;
    title: string;
    typeName: string;
    unitNumber: string;
    buildingName: string;
  };
  company: {
    id: string;
    name: string;
    discipline: string;
  };
  roles: Array<{
    code: string;
    label: string;
    requirements: RoleReq[];
  }>;
  sessionExpiresAt: string;
  maxFileSizeMb: number;
  coiRequirements: string[];
};

/**
 * Walkthrough portal a contractor lands on after exchanging their
 * magic link. Shows the project / unit / company they're acting
 * for, the role(s) they're filling on this project, and a per-role
 * checklist of required documents with format hints + per-doc
 * upload buttons. Replaces the bare upload page they used to figure
 * out themselves.
 */
export function ContractorPortalPage() {
  const nav = useNavigate();
  const [state, setState] = useState<PortalState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }
    if (!stored) {
      setError(
        "Your contractor session has expired or this browser doesn't support local storage. Please open the invite email again to sign in.",
      );
      return;
    }
    setToken(stored);
  }, []);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const res = await fetch(
          `/api/contractor/portal/state?token=${encodeURIComponent(token)}`,
        );
        if (!res.ok) {
          if (res.status === 401) {
            setError(
              "Your contractor session has expired. Open the invite email again to sign in.",
            );
            try {
              localStorage.removeItem(STORAGE_KEY);
            } catch {}
            return;
          }
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          setError(
            `Couldn't load your portal (${body.error ?? `HTTP ${res.status}`}).`,
          );
          return;
        }
        setState((await res.json()) as PortalState);
      } catch (err) {
        setError(String(err));
      }
    })();
  }, [token]);

  const reload = async () => {
    if (!token) return;
    try {
      const res = await fetch(
        `/api/contractor/portal/state?token=${encodeURIComponent(token)}`,
      );
      if (res.ok) setState((await res.json()) as PortalState);
    } catch {
      // silent
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto pt-12 px-4">
        <PageHeader title="Contractor portal" />
        <Card>
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={24}
              className="text-status-rejected-fg flex-shrink-0"
            />
            <div>
              <p className="text-ink-700 text-[14px] leading-relaxed">
                {error}
              </p>
              <p className="text-ink-500 text-[12.5px] mt-2">
                If you need a fresh invite, ask the unit owner to re-send it
                from the project page. Each invite is single-use.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!state || !token) {
    return (
      <div className="max-w-3xl mx-auto pt-12 px-4">
        <PageHeader title="Contractor portal" />
        <Card>
          <p className="text-ink-500">Loading…</p>
        </Card>
      </div>
    );
  }

  const totalReqs = state.roles.reduce((n, r) => n + r.requirements.length, 0);
  const fulfilledReqs = state.roles.reduce(
    (n, r) => n + r.requirements.filter((req) => req.fulfilled).length,
    0,
  );
  const allDone = totalReqs > 0 && fulfilledReqs === totalReqs;

  const signOut = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    nav("/login", { replace: true });
  };

  return (
    <div className="max-w-3xl mx-auto px-4">
      <PageHeader
        eyebrow={state.company.name}
        title={`Documents for ${state.project.typeName}`}
        subtitle={
          <>
            Unit {state.project.unitNumber} · {state.project.buildingName} ·
            link expires {new Date(state.sessionExpiresAt).toLocaleDateString()}
          </>
        }
        actions={
          <button
            type="button"
            onClick={signOut}
            className="text-[12px] text-ink-500 hover:text-ink-700 underline"
          >
            Sign out
          </button>
        }
      />

      <Card>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
            <ClipboardCheck size={18} />
            What to upload
          </h2>
          <span
            className={cx(
              "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold",
              allDone
                ? "bg-status-approved-bg text-status-approved-fg"
                : "bg-status-review-bg text-status-review-fg",
            )}
          >
            {fulfilledReqs}/{totalReqs} complete
          </span>
        </div>
        <p className="text-[13px] text-ink-600 leading-snug mb-4">
          You're listed on this project as{" "}
          <strong className="text-ink-800">
            {state.roles.map((r) => r.label).join(", ") || "—"}
          </strong>
          . Upload the documents below — Property Management will receive each
          one as soon as it clears the virus scan. Files up to{" "}
          {state.maxFileSizeMb}MB; PDF preferred.
        </p>

        {allDone && (
          <div className="alert-success mb-4">
            All required documents are uploaded. The unit owner will be
            notified, and Management will review them with the project.
          </div>
        )}

        <div className="flex flex-col gap-5">
          {state.roles.map((role) => (
            <RoleSection
              key={role.code}
              role={role}
              token={token}
              onUploaded={reload}
            />
          ))}
        </div>
      </Card>

      <ReferenceDocumentsCard roles={state.roles} />

      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
          <Hammer size={18} /> Insurance certificate requirements
        </h2>
        <p className="text-[13px] text-ink-600 mb-2">
          Your COI must include the following — print this list for your carrier
          if you need a fresh certificate:
        </p>
        <ul className="list-disc pl-5 text-ink-700 text-[13px] space-y-1">
          {state.coiRequirements.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </Card>

      <p className="text-center text-[12.5px] text-ink-500 mt-4">
        Questions about a document? See the{" "}
        <Link to="/help/contractor" className="text-brand-700">
          contractor help page
        </Link>
        .
      </p>
    </div>
  );
}

function RoleSection({
  role,
  token,
  onUploaded,
}: {
  role: PortalState["roles"][number];
  token: string;
  onUploaded: () => void | Promise<void>;
}) {
  return (
    <section>
      <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500 mb-2">
        {role.label}
      </div>
      {role.requirements.length === 0 ? (
        <p className="text-[12.5px] text-ink-500">
          No required documents listed for this role on this project.
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {role.requirements.map((req) => (
            <RequirementRow
              key={req.code}
              req={req}
              roleCode={role.code}
              token={token}
              onUploaded={onUploaded}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function RequirementRow({
  req,
  roleCode,
  token,
  onUploaded,
}: {
  req: RoleReq;
  roleCode: string;
  token: string;
  onUploaded: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const fileInput = form.elements.namedItem(
      "file",
    ) as HTMLInputElement | null;
    const file = fileInput?.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("sessionToken", token);
    fd.append("documentTypeCode", req.code);
    fd.append("role", roleCode);
    fd.append("file", file);
    try {
      const res = await fetch("/api/contractor/uploads", {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      form.reset();
      await onUploaded();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={cx(
        "border rounded-md p-3.5",
        req.fulfilled
          ? "bg-status-approved-bg/40 border-status-approved-fg/30"
          : "bg-white border-sand-100",
      )}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          {req.fulfilled ? (
            <CheckCircle2
              size={18}
              className="text-status-approved-fg flex-shrink-0 mt-0.5"
            />
          ) : (
            <Circle size={18} className="text-ink-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-ink-800">
              {req.name}
            </div>
            <div className="text-[12px] text-ink-500 mt-0.5 leading-snug">
              {req.formatHint}
            </div>
            {REQUIREMENT_TEMPLATES[req.code] && (
              <a
                href={REQUIREMENT_TEMPLATES[req.code]!.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] text-brand-700 hover:text-brand-800 mt-1"
              >
                <Download size={12} />
                Download template
                <span className="text-ink-400">
                  ({REQUIREMENT_TEMPLATES[req.code]!.format})
                </span>
              </a>
            )}
          </div>
        </div>
        {!req.fulfilled && (
          <form onSubmit={upload} className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-dashed border-sand-300 bg-sand-50 hover:bg-sand-100 cursor-pointer text-[12.5px] text-ink-700">
              <Upload size={13} />
              Choose file
              <input
                type="file"
                name="file"
                className="hidden"
                accept="application/pdf,image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    e.currentTarget.form?.requestSubmit();
                  }
                }}
              />
            </label>
            {busy && (
              <span className="text-[11.5px] text-ink-500">Uploading…</span>
            )}
          </form>
        )}
      </div>
      {error && (
        <div className="mt-2 text-[12px] text-status-rejected-fg">{error}</div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Reference packet
//
// A single roll-up of every template the contractor needs for this
// phase, keyed off the requirement codes the server actually
// returned. Lets the contractor pull the entire packet in one go
// rather than discovering the templates one row at a time on the
// upload checklist.
//
// Phase 4 will replace the fill-sign-scan-upload loop with DocuSign
// envelopes; the call-out at the bottom of the card sets that
// expectation so contractors aren't surprised when the workflow
// changes.
// ---------------------------------------------------------------------------
function ReferenceDocumentsCard({ roles }: { roles: PortalState["roles"] }) {
  const codes = new Set<string>();
  for (const role of roles) {
    for (const req of role.requirements) {
      if (REQUIREMENT_TEMPLATES[req.code]) codes.add(req.code);
    }
  }
  if (codes.size === 0) return null;

  // Dedupe by URL so the same packet (e.g. construction-packet.docx,
  // which covers multiple requirements) only renders once.
  const seenUrls = new Set<string>();
  const items: Array<{
    code: string;
    label: string;
    url: string;
    format: string;
  }> = [];
  for (const code of codes) {
    const tpl = REQUIREMENT_TEMPLATES[code]!;
    if (seenUrls.has(tpl.url)) continue;
    seenUrls.add(tpl.url);
    items.push({ code, ...tpl });
  }

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <FileText size={18} /> Reference documents for this phase
      </h2>
      <p className="text-[13px] text-ink-600 leading-snug mb-3">
        Download the templates below, fill them out, sign where indicated, and
        upload the scanned copies above. Print or email a copy to your client
        and your insurance agent as needed.
      </p>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li key={item.url}>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-sand-100 bg-sand-50 hover:bg-sand-100 text-[13px] text-ink-800"
            >
              <span className="inline-flex items-center gap-2">
                <Download size={14} className="text-brand-700" />
                {item.label}
              </span>
              <span className="text-[11px] uppercase tracking-wider text-ink-500">
                {item.format}
              </span>
            </a>
          </li>
        ))}
      </ul>
      <div className="mt-3 text-[11.5px] text-ink-500 leading-snug border-t border-sand-100 pt-2.5">
        <strong className="text-ink-700">Coming soon:</strong> these signature
        forms will move to DocuSign envelopes routed straight to the parties
        named on the project — no more fill-scan-upload loop.
      </div>
    </Card>
  );
}
